import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Layers, Plus, Folder as FolderIcon, MoreVertical, Pencil, Copy, Trash2,
  ChevronRight, BookOpen, GraduationCap, LogOut, Home, FileText, Sparkles, Menu, X, Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { PromptDialog, CardDialog } from "@/components/dialogs";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import StudyMode from "@/components/StudyMode";

const HTTP_FORBIDDEN = 403;

function RowMenu({ testId, onRename, onDuplicate, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`${testId}-menu-trigger`}
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded-md text-slate-400 hover:text-[#003366] hover:bg-slate-100 transition-colors"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40 bg-white">
        <DropdownMenuItem data-testid={`${testId}-rename`} onClick={(e) => { e.stopPropagation(); onRename(); }}>
          <Pencil className="h-4 w-4 mr-2" /> Rename
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testId}-duplicate`} onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
          <Copy className="h-4 w-4 mr-2" /> Duplicate
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testId}-delete`} onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-600 focus:text-red-600">
          <Trash2 className="h-4 w-4 mr-2" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [decks, setDecks] = useState([]);
  const [activeDeck, setActiveDeck] = useState(null);
  const [cards, setCards] = useState([]);
  const [studying, setStudying] = useState(false);
  const [sub, setSub] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // dialog state
  const [dialog, setDialog] = useState(null); // {type, entity, id, ...}

  const loadSubscription = useCallback(async () => {
    try {
      const res = await api.get("/subscription");
      setSub(res.data);
    } catch (e) {
      console.error("Failed to load subscription:", e);
    }
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open subscription management.");
      setPortalLoading(false);
    }
  };

  const loadFolders = useCallback(async () => {
    const res = await api.get("/folders");
    setFolders(res.data);
    loadSubscription();
  }, [loadSubscription]);

  const loadDecks = useCallback(async (folderId) => {
    const res = await api.get(`/folders/${folderId}/decks`);
    setDecks(res.data);
    loadSubscription();
  }, [loadSubscription]);

  const loadCards = useCallback(async (deckId) => {
    const res = await api.get(`/decks/${deckId}/cards`);
    setCards(res.data);
    loadSubscription();
  }, [loadSubscription]);

  const guard = async (fn) => {
    try {
      await fn();
    } catch (e) {
      if (e?.response?.status === HTTP_FORBIDDEN) {
        // Close any open create/rename dialog so it doesn't stack under the Upgrade dialog.
        setDialog(null);
        toast.error(e.response.data.detail || "Plan limit reached");
        setShowUpgrade(true);
      } else {
        toast.error("Something went wrong");
      }
    }
  };

  useEffect(() => { loadFolders(); }, [loadFolders]);

  const openFolder = async (f) => {
    setSidebarOpen(false);
    setActiveFolder(f);
    setActiveDeck(null);
    setCards([]);
    await loadDecks(f.id);
  };

  const openDeck = async (d) => {
    setActiveDeck(d);
    await loadCards(d.id);
  };

  const goHome = () => { setSidebarOpen(false); setActiveFolder(null); setActiveDeck(null); setDecks([]); setCards([]); };

  // ---- Folder actions ----
  const createFolder = (name) => guard(async () => {
    await api.post("/folders", { name });
    setDialog(null);
    await loadFolders();
    toast.success("Folder created");
  });
  const renameFolder = async (id, name) => {
    await api.put(`/folders/${id}`, { name });
    setDialog(null);
    await loadFolders();
    if (activeFolder?.id === id) setActiveFolder({ ...activeFolder, name });
    toast.success("Folder renamed");
  };
  const duplicateFolder = (id) => guard(async () => {
    await api.post(`/folders/${id}/duplicate`);
    await loadFolders();
    toast.success("Folder duplicated");
  });
  const deleteFolder = async (id) => {
    await api.delete(`/folders/${id}`);
    if (activeFolder?.id === id) goHome();
    await loadFolders();
    toast.success("Folder deleted");
  };

  // ---- Deck actions ----
  const createDeck = (name) => guard(async () => {
    await api.post(`/folders/${activeFolder.id}/decks`, { name });
    setDialog(null);
    await loadDecks(activeFolder.id);
    toast.success("Deck created");
  });
  const renameDeck = async (id, name) => {
    await api.put(`/decks/${id}`, { name });
    setDialog(null);
    await loadDecks(activeFolder.id);
    if (activeDeck?.id === id) setActiveDeck({ ...activeDeck, name });
    toast.success("Deck renamed");
  };
  const duplicateDeck = (id) => guard(async () => {
    await api.post(`/decks/${id}/duplicate`);
    await loadDecks(activeFolder.id);
    toast.success("Deck duplicated");
  });
  const deleteDeck = async (id) => {
    await api.delete(`/decks/${id}`);
    if (activeDeck?.id === id) { setActiveDeck(null); setCards([]); }
    await loadDecks(activeFolder.id);
    toast.success("Deck deleted");
  };

  // ---- Card actions ----
  const createCard = ({ front, back }) => guard(async () => {
    await api.post(`/decks/${activeDeck.id}/cards`, { front, back });
    setDialog(null);
    await loadCards(activeDeck.id);
    toast.success("Card added");
  });
  const updateCard = async (id, { front, back }) => {
    await api.put(`/cards/${id}`, { front, back });
    setDialog(null);
    await loadCards(activeDeck.id);
    toast.success("Card updated");
  };
  const duplicateCard = (id) => guard(async () => {
    await api.post(`/cards/${id}/duplicate`);
    await loadCards(activeDeck.id);
    toast.success("Card duplicated");
  });
  const deleteCard = async (id) => {
    await api.delete(`/cards/${id}`);
    await loadCards(activeDeck.id);
    toast.success("Card deleted");
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Mobile overlay behind the drawer */}
      {sidebarOpen && (
        <div
          data-testid="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
        />
      )}

      {/* Sidebar (off-canvas drawer on mobile, static on desktop) */}
      <aside
        className={`fixed md:sticky top-0 inset-y-0 left-0 z-50 w-64 shrink-0 border-r border-slate-100 bg-slate-50 flex flex-col h-screen transition-transform duration-200 md:translate-x-0 md:visible ${sidebarOpen ? "translate-x-0 visible" : "-translate-x-full invisible"}`}
      >
        <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-100">
          <Layers className="h-5 w-5 text-[#003366]" />
          <span className="font-display font-extrabold text-lg tracking-tight text-slate-900">Revora</span>
          <button
            data-testid="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            className="md:hidden ml-auto p-1.5 rounded-md text-slate-400 hover:text-[#003366] hover:bg-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Folders</span>
          <button
            data-testid="add-folder-btn"
            onClick={() => setDialog({ type: "create-folder" })}
            className="p-1 rounded-md text-slate-400 hover:text-[#003366] hover:bg-white transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
          <button
            data-testid="sidebar-home"
            onClick={goHome}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${!activeFolder ? "bg-[#003366] text-white" : "text-slate-600 hover:bg-white"}`}
          >
            <Home className="h-4 w-4" /> All folders
          </button>
          {folders.map((f) => (
            <div
              key={f.id}
              className={`group w-full flex items-center gap-1 rounded-lg text-sm transition-colors ${activeFolder?.id === f.id ? "bg-white text-[#003366] font-medium shadow-sm" : "text-slate-600 hover:bg-white"}`}
            >
              <button
                data-testid={`sidebar-folder-${f.id}`}
                onClick={() => openFolder(f)}
                className="flex-1 min-w-0 flex items-center gap-2 pl-3 pr-1 py-2 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003366]"
              >
                <FolderIcon className="h-4 w-4 shrink-0" />
                <span className="truncate">{f.name}</span>
              </button>
              <div className="pr-1">
                <RowMenu
                  testId={`sidebar-folder-${f.id}`}
                  onRename={() => setDialog({ type: "rename-folder", id: f.id, initial: f.name })}
                  onDuplicate={() => duplicateFolder(f.id)}
                  onDelete={() => deleteFolder(f.id)}
                />
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-100 p-3">
          {sub && sub.plan === "free" && (
            <button
              data-testid="plan-upgrade-btn"
              onClick={() => setShowUpgrade(true)}
              className="w-full text-left mb-2 rounded-xl border border-slate-200 bg-white p-3 hover:border-[#003366]/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Plan</span>
                <span data-testid="plan-badge" className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                  Free
                </span>
              </div>
              <div className="mt-2 space-y-1 text-[11px] text-slate-400" data-testid="plan-usage">
                <Usage label="Folders" used={sub.usage.folders} max={sub.limits.folders} />
                <Usage label="Decks" used={sub.usage.decks} max={sub.limits.decks} />
                <Usage label="Cards" used={sub.usage.cards} max={sub.limits.cards} />
              </div>
              <div className="mt-2 flex items-center gap-1 text-xs font-medium text-[#003366]">
                <Sparkles className="h-3.5 w-3.5" /> Upgrade to Premium
              </div>
            </button>
          )}
          {sub && sub.plan === "premium" && (
            <div className="mb-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="plan-card">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Plan</span>
                <span data-testid="plan-badge" className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#003366] text-white">
                  Premium
                </span>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Unlimited everything · £2/mo</p>
              <button
                data-testid="manage-subscription-btn"
                onClick={openPortal}
                disabled={portalLoading}
                className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg border border-[#003366]/30 py-1.5 text-xs font-medium text-[#003366] hover:bg-[#003366] hover:text-white transition-colors disabled:opacity-50"
              >
                <Settings className="h-3.5 w-3.5" />
                {portalLoading ? "Opening…" : "Manage Subscription"}
              </button>
            </div>
          )}
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[#003366] text-white flex items-center justify-center text-sm font-medium">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-white transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 h-14 px-4 border-b border-slate-100 bg-white/90 backdrop-blur">
          <button
            data-testid="sidebar-open-btn"
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 -ml-1.5 rounded-md text-slate-600 hover:text-[#003366] hover:bg-slate-100 transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#003366]" />
            <span className="font-display font-extrabold text-base tracking-tight text-slate-900">Revora</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="h-16 border-b border-slate-100 flex items-center px-4 md:px-8 gap-2 text-sm overflow-x-auto whitespace-nowrap">
          <button onClick={goHome} className="text-slate-500 hover:text-[#003366] transition-colors shrink-0" data-testid="breadcrumb-home">Home</button>
          {activeFolder && (
            <>
              <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
              <button onClick={() => openFolder(activeFolder)} className={`transition-colors truncate max-w-[40vw] md:max-w-none ${activeDeck ? "text-slate-500 hover:text-[#003366]" : "text-slate-900 font-medium"}`} data-testid="breadcrumb-folder">
                {activeFolder.name}
              </button>
            </>
          )}
          {activeDeck && (
            <>
              <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
              <span className="text-slate-900 font-medium truncate max-w-[40vw] md:max-w-none" data-testid="breadcrumb-deck">{activeDeck.name}</span>
            </>
          )}
        </div>

        <div className="p-4 md:p-8 max-w-6xl">
          {/* View: All folders */}
          {!activeFolder && (
            <Section
              title="Your folders"
              subtitle="Group your decks by subject, class, or project."
              actionLabel="New folder"
              actionTestId="new-folder-btn"
              onAction={() => setDialog({ type: "create-folder" })}
            >
              {folders.length === 0 ? (
                <Empty icon={FolderIcon} text="No folders yet. Create your first folder." />
              ) : (
                <>
                  {/* Mobile: vertical list */}
                  <div className="md:hidden space-y-3" data-testid="folders-mobile-list">
                    {folders.map((f) => (
                      <ListRow key={f.id} testId={`folder-row-${f.id}`} onClick={() => openFolder(f)}
                        icon={FolderIcon} title={f.name}
                        menu={<RowMenu testId={`folder-row-${f.id}`}
                          onRename={() => setDialog({ type: "rename-folder", id: f.id, initial: f.name })}
                          onDuplicate={() => duplicateFolder(f.id)}
                          onDelete={() => deleteFolder(f.id)} />}
                      />
                    ))}
                  </div>
                  {/* Desktop: card grid */}
                  <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {folders.map((f) => (
                      <Card key={f.id} testId={`folder-card-${f.id}`} onClick={() => openFolder(f)}
                        icon={FolderIcon} title={f.name}
                        menu={<RowMenu testId={`folder-card-${f.id}`}
                          onRename={() => setDialog({ type: "rename-folder", id: f.id, initial: f.name })}
                          onDuplicate={() => duplicateFolder(f.id)}
                          onDelete={() => deleteFolder(f.id)} />}
                      />
                    ))}
                  </div>
                </>
              )}
            </Section>
          )}

          {/* View: Decks in folder */}
          {activeFolder && !activeDeck && (
            <Section
              title={activeFolder.name}
              subtitle="Decks in this folder."
              actionLabel="New deck"
              actionTestId="new-deck-btn"
              onAction={() => setDialog({ type: "create-deck" })}
            >
              {decks.length === 0 ? (
                <Empty icon={BookOpen} text="No decks yet. Create one to start adding cards." />
              ) : (
                <>
                  {/* Mobile: vertical list */}
                  <div className="md:hidden space-y-3" data-testid="decks-mobile-list">
                    {decks.map((d) => (
                      <ListRow key={d.id} testId={`deck-row-${d.id}`} onClick={() => openDeck(d)}
                        icon={BookOpen} title={d.name}
                        menu={<RowMenu testId={`deck-row-${d.id}`}
                          onRename={() => setDialog({ type: "rename-deck", id: d.id, initial: d.name })}
                          onDuplicate={() => duplicateDeck(d.id)}
                          onDelete={() => deleteDeck(d.id)} />}
                      />
                    ))}
                  </div>
                  {/* Desktop: card grid */}
                  <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {decks.map((d) => (
                      <Card key={d.id} testId={`deck-card-${d.id}`} onClick={() => openDeck(d)}
                        icon={BookOpen} title={d.name}
                        menu={<RowMenu testId={`deck-card-${d.id}`}
                          onRename={() => setDialog({ type: "rename-deck", id: d.id, initial: d.name })}
                          onDuplicate={() => duplicateDeck(d.id)}
                          onDelete={() => deleteDeck(d.id)} />}
                      />
                    ))}
                  </div>
                </>
              )}
            </Section>
          )}

          {/* View: Cards in deck */}
          {activeDeck && (
            <div>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6 md:mb-8">
                <div className="min-w-0">
                  <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-slate-900 break-words">{activeDeck.name}</h1>
                  <p className="text-slate-500 mt-1 text-sm">{cards.length} card{cards.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Button variant="outline" data-testid="study-btn" disabled={cards.length === 0}
                    onClick={() => setStudying(true)}
                    className="rounded-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white disabled:opacity-40">
                    <GraduationCap className="h-4 w-4 mr-2" /> Study
                  </Button>
                  <Button data-testid="add-card-btn" onClick={() => setDialog({ type: "create-card" })}
                    className="rounded-full bg-[#003366] hover:bg-[#00284d] text-white">
                    <Plus className="h-4 w-4 mr-2" /> Add card
                  </Button>
                </div>
              </div>

              {cards.length === 0 ? (
                <Empty icon={FileText} text="No cards yet. Add your first flashcard." />
              ) : (
                <div className="space-y-3">
                  {cards.map((c) => (
                    <div key={c.id} data-testid={`card-row-${c.id}`}
                      className="relative flex flex-col md:flex-row md:items-stretch gap-3 md:gap-4 rounded-xl border border-slate-200 bg-white p-4 pr-10 md:pr-4 hover:-translate-y-[2px] hover:shadow-sm transition-all">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">Front</p>
                        <p className="text-slate-900 font-medium break-words whitespace-pre-wrap">{c.front}</p>
                      </div>
                      <div className="h-px w-full md:h-auto md:w-px bg-slate-100" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">Back</p>
                        <p className="text-slate-700 break-words whitespace-pre-wrap">{c.back}</p>
                      </div>
                      <div className="absolute top-2 right-2 md:static md:self-center">
                        <RowMenu testId={`card-row-${c.id}`}
                          onRename={() => setDialog({ type: "edit-card", id: c.id, front: c.front, back: c.back })}
                          onDuplicate={() => duplicateCard(c.id)}
                          onDelete={() => deleteCard(c.id)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {studying && activeDeck && (
        <StudyMode deckName={activeDeck.name} cards={cards} onExit={() => setStudying(false)} />
      )}

      {/* Dialogs */}
      <PromptDialog open={dialog?.type === "create-folder"} onOpenChange={() => setDialog(null)}
        title="New folder" label="Folder name" confirmText="Create" testId="folder"
        onSubmit={createFolder} />
      <PromptDialog open={dialog?.type === "rename-folder"} onOpenChange={() => setDialog(null)}
        title="Rename folder" label="Folder name" initial={dialog?.initial} confirmText="Rename" testId="folder-rename"
        onSubmit={(name) => renameFolder(dialog.id, name)} />
      <PromptDialog open={dialog?.type === "create-deck"} onOpenChange={() => setDialog(null)}
        title="New deck" label="Deck name" confirmText="Create" testId="deck"
        onSubmit={createDeck} />
      <PromptDialog open={dialog?.type === "rename-deck"} onOpenChange={() => setDialog(null)}
        title="Rename deck" label="Deck name" initial={dialog?.initial} confirmText="Rename" testId="deck-rename"
        onSubmit={(name) => renameDeck(dialog.id, name)} />
      <CardDialog open={dialog?.type === "create-card"} onOpenChange={() => setDialog(null)}
        title="Add flashcard" confirmText="Add" onSubmit={createCard} />
      <CardDialog open={dialog?.type === "edit-card"} onOpenChange={() => setDialog(null)}
        title="Edit flashcard" initialFront={dialog?.front} initialBack={dialog?.back} confirmText="Save"
        onSubmit={(v) => updateCard(dialog.id, v)} />

      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade}
        plan={sub?.plan} usage={sub?.usage} />
    </div>
  );
}

function Usage({ label, used, max }) {
  const pct = Math.min(100, (used / max) * 100);
  return (
    <div>
      <div className="flex items-center justify-between">
        <span>{label}</span>
        <span className="tabular-nums">{used}/{max}</span>
      </div>
      <div className="mt-0.5 h-1 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${pct >= 100 ? "bg-red-400" : "bg-[#003366]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Section({ title, subtitle, actionLabel, actionTestId, onAction, children }) {
  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6 md:mb-8">
        <div className="min-w-0">
          <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-slate-900 break-words">{title}</h1>
          <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>
        </div>
        <Button data-testid={actionTestId} onClick={onAction}
          className="rounded-full bg-[#003366] hover:bg-[#00284d] text-white shrink-0 self-start">
          <Plus className="h-4 w-4 mr-2" /> {actionLabel}
        </Button>
      </div>
      {children}
    </div>
  );
}

function ListRow({ testId, onClick, icon: Icon, title, menu }) {
  return (
    <div data-testid={testId} onClick={onClick}
      className="relative flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 pr-2 cursor-pointer active:bg-slate-50 transition-colors">
      <div className="h-10 w-10 shrink-0 rounded-lg bg-[#003366]/10 text-[#003366] flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <p className="flex-1 min-w-0 font-display font-bold text-base text-slate-900 tracking-tight break-words whitespace-normal">
        {title}
      </p>
      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
      {menu}
    </div>
  );
}

function Card({ testId, onClick, icon: Icon, title, menu }) {
  return (
    <div data-testid={testId} onClick={onClick}
      className="group relative rounded-2xl border border-slate-200 bg-white p-6 cursor-pointer hover:-translate-y-[2px] hover:shadow-md hover:border-[#003366]/30 transition-all">
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
        {menu}
      </div>
      <div className="h-11 w-11 rounded-xl bg-[#003366]/10 text-[#003366] flex items-center justify-center mb-6">
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-display font-bold text-lg text-slate-900 tracking-tight truncate">{title}</p>
      <p className="text-sm text-slate-400 mt-1 flex items-center gap-1">
        Open <ChevronRight className="h-3.5 w-3.5" />
      </p>
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div data-testid="empty-state" className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-20 flex flex-col items-center justify-center text-center">
      <div className="h-14 w-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-slate-400" />
      </div>
      <p className="text-slate-500 max-w-xs">{text}</p>
    </div>
  );
}
